import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobsAPI } from '../services/api';

interface JobFormData {
  title: string;
  description: string;
  experienceRequired: number | null;
  mustHaveSkills: string;
  niceToHaveSkills: string;
  positionLocation: string;
  jobType: string;
  salaryRange: string;
  employmentType: string;
  remoteWorkAllowed: boolean;
  applicationDeadline: string;
  jobStatus: string;
  qualificationRequired: string;
}

const EditJobPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<JobFormData>({
    title: '',
    description: '',
    experienceRequired: 0,
    mustHaveSkills: '',
    niceToHaveSkills: '',
    positionLocation: '',
    jobType: 'FULL_TIME',
    salaryRange: '',
    employmentType: 'ON_SITE',
    remoteWorkAllowed: false,
    applicationDeadline: '',
    jobStatus: 'ACTIVE',
    qualificationRequired: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const fetchJobData = async () => {
      if (!jobId) {
        setError('Job ID is missing.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await jobsAPI.getJob(Number(jobId));
        const jobData = response.data;

        setFormData({
          title: jobData.title || '',
          description: jobData.description || '',
          experienceRequired: jobData.experienceRequired ?? 0,
          mustHaveSkills: Array.isArray(jobData.requiredSkills) ? jobData.requiredSkills.join(', ') : '',
          niceToHaveSkills: Array.isArray(jobData.niceToHaveSkills) ? jobData.niceToHaveSkills.join(', ') : '',
          positionLocation: jobData.positionLocation || '',
          jobType: jobData.jobType || 'FULL_TIME',
          salaryRange: `${jobData.salaryMin}-${jobData.salaryMax}`,
          employmentType: jobData.employmentType || 'ON_SITE',
          remoteWorkAllowed: jobData.remoteWorkAllowed || false,
          applicationDeadline: jobData.applicationDeadline ? new Date(jobData.applicationDeadline).toISOString().split('T')[0] : '',
          jobStatus: jobData.status || 'ACTIVE',
          qualificationRequired: jobData.qualificationRequired || ''
        });
        setLoading(false);
      } catch (err) {
        setError('Failed to load job details. Please try again.');
        console.error(err);
        setLoading(false);
      }
    };

    fetchJobData();
  }, [jobId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
        const { checked } = e.target as HTMLInputElement;
        setFormData(prev => ({
            ...prev,
            [name]: checked
        }));
    } else {
        setFormData(prev => ({
            ...prev,
            [name]: type === "number" ? (value === "" ? null : Number(value)) : value
        }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!jobId) {
      setError('Job ID is missing for update operation.');
      return;
    }

    try {
      const postData = {
        ...formData,
        mustHaveSkills: formData.mustHaveSkills.split(',').map(skill => skill.trim()).filter(skill => skill !== ''),
        niceToHaveSkills: formData.niceToHaveSkills.split(',').map(skill => skill.trim()).filter(skill => skill !== ''),
      };
      
      const response = await jobsAPI.updateJob(Number(jobId), postData);
      setSuccess('Job updated successfully!');
      console.log(response.data);
      // Optionally navigate back to dashboard or job details page
      navigate('/dashboard');
    } catch (err) {
      setError('Failed to update job. Please try again.');
      console.error(err);
    }
  };

  if (loading) {
    return <div className="container mx-auto p-4">Loading job details...</div>; // Or a SkeletonLoader
  }

  if (error && !success) {
    return <div className="container mx-auto p-4 text-red-500">Error: {error}</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Edit Job Posting</h1>
      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">{error}</div>}
      {success && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">{success}</div>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-700">Role</label>
          <input type="text" name="role" id="role" value={formData.title} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" required />
        </div>
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
          <textarea name="description" id="description" value={formData.description} onChange={handleChange} rows={4} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" required />
        </div>
        <div>
          <label htmlFor="experienceRequired" className="block text-sm font-medium text-gray-700">Experience Required (years)</label>
          <input type="number" name="experienceRequired" id="experienceRequired" value={formData.experienceRequired ?? ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" required />
        </div>
        <div>
          <label htmlFor="mustHaveSkills" className="block text-sm font-medium text-gray-700">Must-Have Skills (comma-separated)</label>
          <input type="text" name="mustHaveSkills" id="mustHaveSkills" value={formData.mustHaveSkills} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" required />
        </div>
        <div>
          <label htmlFor="niceToHaveSkills" className="block text-sm font-medium text-gray-700">Nice-To-Have Skills (comma-separated)</label>
          <input type="text" name="niceToHaveSkills" id="niceToHaveSkills" value={formData.niceToHaveSkills} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
        </div>
        <div>
          <label htmlFor="qualificationRequired" className="block text-sm font-medium text-gray-700">Qualification Required</label>
          <input type="text" name="qualificationRequired" id="qualificationRequired" value={formData.qualificationRequired} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
        </div>
        <div>
          <label htmlFor="positionLocation" className="block text-sm font-medium text-gray-700">Location</label>
          <input type="text" name="positionLocation" id="positionLocation" value={formData.positionLocation} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" required />
        </div>
        <div>
          <label htmlFor="jobType" className="block text-sm font-medium text-gray-700">Job Type</label>
          <select name="jobType" id="jobType" value={formData.jobType} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
            <option>FULL_TIME</option>
            <option>PART_TIME</option>
            <option>CONTRACT</option>
            <option>TEMPORARY</option>
            <option>INTERNSHIP</option>
          </select>
        </div>
        <div>
          <label htmlFor="salaryRange" className="block text-sm font-medium text-gray-700">Salary Range (e.g., 50000-70000)</label>
          <input type="text" name="salaryRange" id="salaryRange" value={formData.salaryRange} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" required />
        </div>
        <div>
          <label htmlFor="employmentType" className="block text-sm font-medium text-gray-700">Employment Type</label>
          <select name="employmentType" id="employmentType" value={formData.employmentType} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
            <option>REMOTE</option>
            <option>HYBRID</option>
            <option>ON_SITE</option>
          </select>
        </div>
        <div className="flex items-center">
          <input type="checkbox" name="remoteWorkAllowed" id="remoteWorkAllowed" checked={formData.remoteWorkAllowed} onChange={handleChange} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
          <label htmlFor="remoteWorkAllowed" className="ml-2 block text-sm text-gray-900">Remote Work Allowed</label>
        </div>
        <div>
          <label htmlFor="applicationDeadline" className="block text-sm font-medium text-gray-700">Application Deadline</label>
          <input type="date" name="applicationDeadline" id="applicationDeadline" value={formData.applicationDeadline} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" required />
        </div>
        <div>
          <label htmlFor="jobStatus" className="block text-sm font-medium text-gray-700">Job Status</label>
          <select name="jobStatus" id="jobStatus" value={formData.jobStatus} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
            <option>ACTIVE</option>
            <option>PAUSED</option>
            <option>CLOSED</option>
            <option>EXPIRED</option>
            <option>DRAFT</option>
          </select>
        </div>
        <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          Update Job
        </button>
      </form>
    </div>
  );
};

export default EditJobPage;
